import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Calendar as CalendarIcon, Clock, MessageCircle, Video } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";

const SLOTS = ["10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00"];
const ro = { months: ["Ianuarie","Februarie","Martie","Aprilie","Mai","Iunie","Iulie","August","Septembrie","Octombrie","Noiembrie","Decembrie"] };

export default function Booking() {
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState("audio-30");
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", whatsapp: "", notes: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/booking/services").then(r => setServices(r.data || [])).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) { toast.error("Alege o zi și o oră"); return; }
    if (!form.name || !form.email || !form.phone) { toast.error("Completează datele de contact"); return; }
    setLoading(true);
    try {
      const dateStr = date.toISOString().slice(0, 10);
      const { data } = await api.post("/booking/checkout", {
        service_id: serviceId, ...form, date: dateStr, time_slot: slot,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la programare");
      setLoading(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div data-testid="page-booking" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <header className="mb-12 text-center">
        <p className="text-xs tracking-[0.3em] text-[#D4A63D] uppercase mb-4">Ghidare personală</p>
        <h1 className="font-display text-4xl md:text-6xl text-[#F8F8F4]">Vorbește cu Gabriel</h1>
        <p className="mt-5 font-quote text-xl md:text-2xl text-[#F8F8F4] max-w-2xl mx-auto">„Uneori, câteva direcții clare pot schimba felul în care vezi tot ce trăiești.”</p>
        <p className="mt-4 text-xs text-[#6C7A9C] max-w-xl mx-auto">Discuțiile au scop informativ și de orientare. Nu înlocuiesc consultanța juridică sau psihologică.</p>
      </header>

      <div className="grid md:grid-cols-3 gap-4 mb-10">
        {services.map(s => (
          <button
            key={s.id}
            data-testid={`svc-${s.id}`}
            onClick={() => setServiceId(s.id)}
            className={`text-left cinematic-card rounded-lg p-5 transition-all ${serviceId === s.id ? "border-[#D4A63D]" : ""}`}
          >
            <div className="flex items-center gap-2 mb-2">
              {s.id.startsWith("video") ? <Video className="w-5 h-5 text-[#D4A63D]" /> : <MessageCircle className="w-5 h-5 text-[#D4A63D]" />}
              <span className="text-xs uppercase tracking-widest text-[#A0AABF]">{s.duration} minute</span>
            </div>
            <div className="font-display text-xl text-[#F8F8F4]">{s.title}</div>
            <div className="mt-3 gold-text font-display text-3xl">{s.price} RON</div>
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="grid md:grid-cols-2 gap-6">
        <div className="cinematic-card rounded-lg p-6">
          <h3 className="font-display text-xl text-[#F8F8F4] mb-4 flex items-center gap-2"><CalendarIcon className="w-5 h-5 text-[#D4A63D]" /> Alege ziua</h3>
          <div className="bg-[#0A1020] rounded-sm p-2">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < today}
              className="text-[#F8F8F4]"
              data-testid="booking-calendar"
            />
          </div>

          <h3 className="font-display text-xl text-[#F8F8F4] mt-6 mb-3 flex items-center gap-2"><Clock className="w-5 h-5 text-[#D4A63D]" /> Alege ora</h3>
          <div className="grid grid-cols-3 gap-2">
            {SLOTS.map(s => (
              <button
                key={s}
                type="button"
                data-testid={`slot-${s}`}
                onClick={() => setSlot(s)}
                className={`px-3 py-2 text-sm rounded-sm border ${slot === s ? "bg-[#D4A63D] text-[#0A1020] border-[#D4A63D]" : "border-white/10 text-[#F8F8F4] hover:border-[#D4A63D]/40"}`}
              >{s}</button>
            ))}
          </div>
        </div>

        <div className="cinematic-card rounded-lg p-6 space-y-3">
          <h3 className="font-display text-xl text-[#F8F8F4]">Datele tale</h3>
          <input data-testid="bk-name" required placeholder="Nume *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-3 text-sm" />
          <input data-testid="bk-email" required type="email" placeholder="Email *" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-3 text-sm" />
          <input data-testid="bk-phone" required placeholder="Telefon *" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-3 text-sm" />
          <input data-testid="bk-wa" placeholder="WhatsApp (dacă diferit)" value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-3 text-sm" />
          <textarea data-testid="bk-notes" rows={4} placeholder="Context scurt (opțional)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-3 text-sm" />

          {date && slot && (
            <div className="text-sm text-[#D4A63D] bg-[#D4A63D]/10 border border-[#D4A63D]/20 rounded-sm p-3">
              {date.toLocaleDateString("ro-RO", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} la <strong>{slot}</strong>
            </div>
          )}

          <button data-testid="bk-submit" disabled={loading} className="btn-gold rounded-sm py-4 w-full text-sm uppercase tracking-widest font-medium disabled:opacity-50">
            {loading ? "Se procesează..." : "Plătește & confirmă"}
          </button>

          <p className="text-xs text-[#6C7A9C] text-center pt-2">Plata securizată prin Stripe. Confirmarea vine pe WhatsApp.</p>
        </div>
      </form>
    </div>
  );
}
